import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiService } from '../services/geminiService';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { geminiApi } from '../services/geminiApi';
import DeploymentOptions from '../components/DeploymentOptions';
import { API_URL } from '../utils/env';

export function ResumeBuilder() {
  const [resumeInput, setResumeInput] = useState('');
  const [resumeStyle, setResumeStyle] = useState('professional');
  const [resumeFormat, setResumeFormat] = useState('chronological');
  const [optimizationFocus, setOptimizationFocus] = useState('ats');
  const [generatedResume, setGeneratedResume] = useState('');
  const [resumeExplanation, setResumeExplanation] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const resumeStyleOptions = [
    { value: 'professional', label: 'Professional', description: 'Traditional corporate style' },
    { value: 'creative', label: 'Creative', description: 'Modern design for creative fields' },
    { value: 'executive', label: 'Executive', description: 'Senior leadership focus' },
    { value: 'technical', label: 'Technical', description: 'Technical role emphasis' },
    { value: 'academic', label: 'Academic', description: 'Research and education focus' },
    { value: 'minimal', label: 'Minimal', description: 'Clean, concise layout' }
  ];

  const resumeFormatOptions = [
    { value: 'chronological', label: 'Chronological', description: 'Traditional timeline format' },
    { value: 'functional', label: 'Functional', description: 'Skills-based format' },
    { value: 'combination', label: 'Combination', description: 'Hybrid of chronological and functional' },
    { value: 'targeted', label: 'Targeted', description: 'Role-specific format' }
  ];

  const optimizationOptions = [
    { value: 'ats', label: 'ATS Optimized' },
    { value: 'readability', label: 'Human Readable' },
    { value: 'keywords', label: 'Keyword Optimized' },
    { value: 'impact', label: 'Impact Focused' }
  ];

  const quickStartGuides = [
    {
      title: "For Recent Graduates",
      description: "Create a compelling resume with limited work experience.",
      keywords: ["Education Focus", "Skills Highlight", "Projects"],
      example: "Example: Emphasize academic achievements and relevant coursework",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
        </svg>
      )
    },
    {
      title: "For Career Changers",
      description: "Highlight transferable skills and relevant experience.",
      keywords: ["Skills Translation", "Achievement Focus", "Industry Bridge"],
      example: "Example: Emphasize transferable leadership and project management skills",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )
    },
    {
      title: "For Executives",
      description: "Showcase leadership and strategic impact.",
      keywords: ["Leadership", "Strategy", "Business Impact"],
      example: "Example: Highlight organizational transformation and P&L responsibility",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setResumeInput(transcribedText);
  };

  const parseGeneratedResponse = (response) => {
    // Split response into Resume and explanation sections
    const sections = response.split(/(?=Explanation:|Optimization Notes:|Best Practices:|ATS Considerations:)/i);
    
    // First section should be the resume content
    const resumeContent = sections[0].trim();
    
    // Combine remaining sections into explanation
    const explanation = sections.slice(1).join('\n\n').trim();
    
    return { resumeContent, explanation };
  };

  const generateResume = async () => {
    if (!resumeInput.trim()) {
      toast.error('Please provide your experience and requirements');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Generate a ${resumeStyle} resume in ${resumeFormat} format for: ${resumeInput}
Style: ${resumeStyle}
Format: ${resumeFormat}
Optimization Focus: ${optimizationFocus}

Please provide:
- The formatted resume content (start with "Resume Content:" on a new line)
- Explanation of the format and structure (start with "Explanation:" on a new line)
- Optimization notes for ${optimizationFocus} (start with "Optimization Notes:" on a new line)
- Best practices applied (start with "Best Practices:" on a new line)
- ATS considerations (start with "ATS Considerations:" on a new line)

Format the response with clear sections and include proper formatting for the resume.`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        const generatedText = response.content || response.message;
        if (generatedText) {
          const { resumeContent, explanation } = parseGeneratedResponse(generatedText);
          setGeneratedResume(resumeContent);
          setResumeExplanation(explanation);
          toast.success('Resume generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating resume:', error);
      toast.error(error.message || 'Failed to generate resume. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-purple-500/40 via-purple-300/10 to-purple-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-purple-400/40 via-purple-300/10 to-purple-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-purple-800/50 border border-purple-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></div>
                <span className="text-sm text-purple-100">AI-Powered Resume</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                Resume Builder
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-purple-200 max-w-xl">
                Create professional, ATS-optimized resumes with AI assistance
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-purple-200">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multiple Formats</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-purple-200">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>ATS Optimized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Target Audience Banner */}
          <div className="mb-8 p-4 bg-purple-50 rounded-lg">
            <h3 className="text-lg font-medium text-purple-900 mb-2">Who is this tool for?</h3>
            <ul className="list-disc list-inside text-purple-700 space-y-1">
              <li>Recent Graduates - Create your first professional resume</li>
              <li>Career Changers - Highlight transferable skills</li>
              <li>Experienced Professionals - Optimize your executive resume</li>
            </ul>
          </div>

          <div className="space-y-8">
            {/* Resume Input with Example */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience & Requirements
              </label>
              <div className="mb-2">
                <div className="text-sm text-gray-500">Example: "10 years of software development experience, specializing in full-stack web development. Led teams of 5-10 developers, delivered enterprise projects, and improved system performance by 40%."</div>
              </div>
              <textarea
                value={resumeInput}
                onChange={(e) => setResumeInput(e.target.value)}
                rows="4"
                placeholder="Describe your experience, skills, and career highlights..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Resume Style Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume Style
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {resumeStyleOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setResumeStyle(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      resumeStyle === option.value
                        ? 'bg-purple-50 border-purple-500 text-purple-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Resume Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume Format
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {resumeFormatOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setResumeFormat(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      resumeFormat === option.value
                        ? 'bg-purple-50 border-purple-500 text-purple-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optimization Focus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optimization Focus
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {optimizationOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setOptimizationFocus(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      optimizationFocus === option.value
                        ? 'bg-purple-50 border-purple-500 text-purple-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generateResume}
              disabled={isGenerating}
              className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                isGenerating
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating Resume...
                </div>
              ) : (
                'Generate Resume'
              )}
            </button>

            {/* Generated Resume Section */}
            {generatedResume ? (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Generated Resume</h3>
                  <button
                    onClick={() => copyToClipboard(generatedResume)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Resume Content */}
                  <div className="prose max-w-none">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Resume Content</h4>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">
                        {generatedResume}
                      </pre>
                    </div>
                  </div>
                  
                  {/* Resume Explanation */}
                  <div className="prose max-w-none">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Resume Analysis</h4>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 overflow-y-auto max-h-[500px]">
                      <div className="markdown-content">
                        {resumeExplanation.split('\n').map((line, index) => {
                          // Check if line is a section header
                          if (line.match(/^(Explanation|Optimization Notes|Best Practices|ATS Considerations):/i)) {
                            return <h5 key={index} className="font-medium text-gray-900 mt-4 mb-2">{line}</h5>;
                          }
                          return <p key={index} className="text-gray-600 mb-2">{line}</p>;
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No resume generated yet</h3>
                <p className="mt-1 text-sm text-gray-500">Start by describing your experience and requirements.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add API Documentation */}
      <div className="mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">API Documentation</h2>
            
            <div className="space-y-6">
              {/* Endpoint Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Endpoint</h3>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                  POST {`${API_URL}/api/resume-builder/generate`}
                </div>
              </div>

              {/* Request Format */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Format</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="language-json">
                    <code>{JSON.stringify({
                      resumeInput: "10 years of software development experience...",
                      resumeStyle: "professional",
                      resumeFormat: "chronological",
                      optimizationFocus: "ats",
                      includeExplanation: true,
                      apiKey: "YOUR_API_KEY" // Optional if using API key authentication
                    }, null, 2)}</code>
                  </pre>
                </div>
              </div>

              {/* Parameters */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Parameters</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parameter</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">resumeInput</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Experience, skills, and career highlights</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">resumeStyle</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Style of resume (professional, creative, executive, etc.)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">resumeFormat</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Format type (chronological, functional, combination)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">optimizationFocus</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Optimization target (ats, readability, keywords, impact)</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">includeExplanation</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">boolean</td>
                        <td className="px-6 py-4 text-sm text-gray-500">Whether to include format and structure explanation</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example cURL Request */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">API Request Examples</h3>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">cURL</h4>
                  <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                    {`curl --location '${API_URL}/api/resume-builder/generate' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer YOUR_API_KEY' \\
--data '{
    "resumeInput": "10 years of software development experience, specializing in full-stack web development. Led teams of 5-10 developers, delivered enterprise projects, and improved system performance by 40%",
    "resumeStyle": "professional",
    "resumeFormat": "chronological",
    "optimizationFocus": "ats",
    "includeExplanation": true
}'`}
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">PowerShell</h4>
                  <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                    {`$headers = @{
    'Content-Type' = 'application/json'
    'Authorization' = 'Bearer YOUR_API_KEY'
}

$body = @{
    resumeInput = "10 years of software development experience, specializing in full-stack web development. Led teams of 5-10 developers, delivered enterprise projects, and improved system performance by 40%"
    resumeStyle = "professional"
    resumeFormat = "chronological"
    optimizationFocus = "ats"
    includeExplanation = $true
} | ConvertTo-Json

Invoke-RestMethod -Method Post -Uri '${API_URL}/api/resume-builder/generate' -Headers $headers -Body $body`}
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Python</h4>
                  <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                    {`import requests
import json

url = '${API_URL}/api/resume-builder/generate'
headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
}
data = {
    "resumeInput": "10 years of software development experience, specializing in full-stack web development. Led teams of 5-10 developers, delivered enterprise projects, and improved system performance by 40%",
    "resumeStyle": "professional",
    "resumeFormat": "chronological",
    "optimizationFocus": "ats",
    "includeExplanation": True
}

response = requests.post(url, headers=headers, json=data)
print(response.json())`}
                  </div>
                </div>
              </div>

              {/* Response Format */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Format</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="language-json">
                    <code>{JSON.stringify({
                      resumeContent: "Generated resume content with proper formatting...",
                      explanation: {
                        format: "Explanation of the format and structure",
                        optimization: "Optimization notes for the chosen focus",
                        bestPractices: "Applied best practices",
                        atsConsiderations: "ATS-specific considerations"
                      }
                    }, null, 2)}</code>
                  </pre>
                </div>
              </div>

              {/* Rate Limits */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Rate Limits & Usage</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>Maximum request timeout: 300 seconds</li>
                  <li>Maximum concurrent requests: 80</li>
                  <li>Memory per instance: 1GB</li>
                  <li>CPU: 1 core</li>
                </ul>
              </div>

              {/* Error Handling */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Responses</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">400 Bad Request</h4>
                    <pre className="bg-gray-50 rounded-lg p-4 mt-2">
                      <code>{JSON.stringify({
                        detail: {
                          error: "Validation error",
                          message: "Required field missing or invalid"
                        }
                      }, null, 2)}</code>
                    </pre>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">500 Internal Server Error</h4>
                    <pre className="bg-gray-50 rounded-lg p-4 mt-2">
                      <code>{JSON.stringify({
                        detail: {
                          error: "Failed to generate resume",
                          message: "Internal server error"
                        }
                      }, null, 2)}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="resume-builder" position="none" />
    </div>
  );
}

export default ResumeBuilder; 